import * as yup from 'yup'
import { useContext, useEffect } from 'react'
import { useSnackbar } from 'notistack'
import { Box, Button } from '@mui/material'
import { useTranslation } from 'react-i18next'
import PersonIcon from '@mui/icons-material/Person'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, FormProvider, useWatch } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'

import { useMeta } from '@contexts/Meta'
import useAPIFetch from '@hooks/useAPIFetch'
import Header from '@components/configurations/header'
import UserService from '@services/users/user.service'
import { InitRoleInfo, RoleInfo } from '@models/role.model'
import { FormTextField } from '@components/configurations/form/textfield'
import ValidationToast from '@components/configurations/form/ValidationToast'
import { CompactFieldRow } from '@components/configurations/form/compactFieldRow'
import AddingUsersTable from '@components/configurations/users_config/user_groups/form/addingUser'
import { ReactComponent as ProjectAccessSettingIcon } from '@assets/sideMenuIcon/project_access_setting.svg'
import { PermissionSettingTable } from '@components/configurations/users_config/user_groups/form/permissionSettingTable'
import CommonService from '@services/common/common.service'
import { OptionsContext } from '../../../contexts/Options/provider'
import { FormSwitch } from '@components/configurations/form/switch'
import ArrowBackIosRoundedIcon from '@mui/icons-material/ArrowBackIosRounded'
import { MdPeopleAlt } from 'react-icons/md'
import { hasPermission } from '@utils/permission_helper'
import { PermissionCodeEnum } from '@models/permission'
import { AuthContext } from '@contexts/Auth'

export const CreateUserGroups = () => {
	const navigate = useNavigate()
	const { t } = useTranslation()
	const { project_id, role_id } = useParams<string>()
	const { setRequest, isLoading } = useAPIFetch()
	const [, { setMeta }] = useMeta()
	const { enqueueSnackbar } = useSnackbar()
	const { state, dispatch } = useContext(OptionsContext)
	const { state: authState } = useContext(AuthContext)

	const validationSchema = yup
		.object()
		.shape({
			code: yup.string().max(255).trim().required('Role Name is required'),
			active: yup.bool()
		})
		.required()

	const methods = useForm<RoleInfo>({
		defaultValues: { ...InitRoleInfo },
		resolver: yupResolver(validationSchema),
		mode: 'all',
		reValidateMode: 'onChange',
		criteriaMode: 'all',
	})
	const { reset, handleSubmit, control, getValues } = methods

	const project_product_subs = useWatch({
		control,
		name: 'project_product_subs',
	})

	const onSubmit = (data: RoleInfo) => {
		const item_subs = data.project_product_subs
			?.filter((elem: any) => elem.isSubscribe)
			?.map((elem: any) => {
				return {
					item_type: elem.item_type,
					item_id: elem.item_id,
				}
			})
		data.project_subs = item_subs
		data.display_name = data.code
		data.project_id = project_id
		data.inherited_role_ids = []
		data.role_rights_list = []
		setRequest({
			callback: async (cancelToken: any) => {
				await UserService.CreateUserGroup(data).then(() => {
					enqueueSnackbar('User role created', { variant: 'success' })
					setMeta({ isLoading: false })

					CommonService.GetCategoryMetadata('role')
						.then((results) => {
							dispatch({ type: 'updateRole', options: results })
						})
						.then(() => {
							navigate(-1)
						})
				})
			},
		})
	}

	const onError = (errors: any) => {
		console.log('errors', errors)
	}

	const reload = async () => {
		setRequest({
			callback: async () => {
				if (project_id) {
					await UserService.GetProject(project_id)
						.then(async (f) => {
							if (f) {
								const project_product_subs = f.subscriptions?.map((elem: any) => {
									elem.isSubscribe = false
									return elem
								})

								reset({
									project_product_subs: project_product_subs,
									role_profile_ids: [],
									active: true,
								})
							}
						})
						.catch((err) => {
							enqueueSnackbar(err.response.data.message, {
								variant: 'error',
							})
							navigate(`/configurations/users_config/user_groups/${project_id}`)
						})
				}
			},
		})
	}

	useEffect(() => {
		setMeta({ isLoading })
	}, [isLoading])

	useEffect(() => {
		reload()
	}, [])

	return (
		<>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				<ArrowBackIosRoundedIcon
					fontSize={'large'}
					sx={{ cursor: 'pointer', color: '#707070' }}
					onClick={() => {
						navigate(`/configurations/users_config/user_groups/${project_id}`)
					}}
				/>
				<Header title={t('Create User Role')} icon={<MdPeopleAlt size={30} color={'#707070'} />} />
			</Box>

			<Box component="form" onSubmit={handleSubmit(onSubmit, onError)} sx={{ marginX: '45px' }}>
				<FormProvider {...methods}>
					<CompactFieldRow
						firstchildren={
							<FormTextField
								name="code"
								control={control}
								label={t('Role Name')}
								mandatory={true}
							/>
						}
						// secondchildren={<></>}
						secondchildren={<FormSwitch name="is_admin" control={control} label={t('Admin')} />}
						// secondchildren={<FormSwitch name="active" control={control} label={t('Active')} />}
					/>

					<Box
						sx={{
							marginTop: '25px',
						}}
					>
						<Header
							title={t('Permission Settings')}
							icon={<ProjectAccessSettingIcon style={{ width: '25px' }} />}
						/>
						<PermissionSettingTable
							control={control}
							permissionSettingItems={project_product_subs}
						/>
					</Box>

					{hasPermission(authState?.userInfo, [PermissionCodeEnum.RoleAssignUser]) && (
						<AddingUsersTable />
					)}

					<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '12px' }}>
						<Button variant="contained" color="defaultButton" type="submit" disabled={isLoading}>
							{t('Confirm')}
						</Button>
						<Button
							variant="contained"
							color="error"
							onClick={() => navigate(-1)}
							disabled={isLoading}
						>
							{t('Cancel')}
						</Button>
					</Box>
				</FormProvider>
				<ValidationToast control={control} />
			</Box>
		</>
	)
}
